import { lazy, ReactNode, Suspense, useEffect, useState } from "react";
import { fetchAuthSession, fetchUserAttributes } from "aws-amplify/auth";
import { useDispatch, useSelector } from "react-redux";
import { Logomark } from "@/common/Logo/Logo.tsx";
import {
    getSelectedWorkspace,
    setSelectedWorkspace,
} from "@/features/Workspace/Workspace.slice.ts";
import { useUserVerification } from "@/app/Authentication.slice.ts";
import { useAuth } from "@/app/auth/AuthContext.tsx";
import { useAuthSurface } from "@/app/auth/authSurface.ts";
import { auditTokenOf } from "@/app/audit/auditProspect.ts";
import { forgetSignInWithoutPasskey } from "@/app/auth/passkeyNudge.ts";
import { useStartSessionMutation } from "@/services/apis/api.ts";
import { isApiError } from "@/services/apis/apiError";
import { deleteCookie } from "@/util/cookie.ts";
import { GA_SESSION_ID_COOKIE } from "@/app/auth/gaSession.ts";
import Loading from "@/common/Loading";
import BootSkeleton, { hasStoredSession } from "@/common/BootSkeleton";
import Button from "@/common/Button";
import Panel from "@/common/Panel/Panel.tsx";
import { clearCognitoLocalStorage, useSignOut } from "@/hooks/use-signout.ts";
import { continueWithSingleSignOn } from "@/app/auth/ssoDiscovery.ts";
import { preloadable } from "@/util/preloadable.tsx";
import { useRequiredData } from "@/app/RequiredDataProvider.tsx";

/**
 * The sign-in screens, out of the bundle a signed-in reload runs. main.tsx
 * preloads them when no session is stored, so a visitor gets them without a
 * fallback.
 */
export const authFlow = preloadable(() => import("@/app/auth/AuthFlow.tsx"));

// Rare: an audit link opened while signed in, and a changed email to verify
const AuditSignedInFlow = lazy(
    () => import("@/app/audit/AuditSignedInFlow.tsx"),
);
const EmailCodeVerification = lazy(
    () => import("@/app/auth/EmailCodeVerification.tsx"),
);

export default function CognitoAuthenticator({
    children,
    restoring = <Loading />,
}: {
    children: ReactNode;
    /** Shown while a stored session is restored: the app's empty frame (BootSkeleton) */
    restoring?: ReactNode;
}) {
    const { authStatus, isResolvingRedirect } = useAuth();
    const { serverSession } = useAuthSurface();
    const [needsVerification, setNeedsVerification] = useUserVerification();
    const dispatch = useDispatch();

    useEffect(() => {
        if (needsVerification || authStatus !== "authenticated") {
            return;
        }
        Promise.all([fetchUserAttributes(), fetchAuthSession()]).then(
            ([attributes, session]) => {
                const idToken = session.tokens?.idToken;
                const identities = idToken?.payload?.identities;

                if (identities?.[0 as keyof typeof identities]) {
                    // This is a social login
                    return;
                }

                if (attributes.email_verified === "false") {
                    setNeedsVerification(true);
                }
            },
        );
    }, [needsVerification, setNeedsVerification, authStatus]);

    useEffect(() => {
        if (authStatus === "unauthenticated") {
            dispatch(setSelectedWorkspace(undefined));
            // However the last session ended, the next sign-in starts clean.
            forgetSignInWithoutPasskey();
        }
    }, [authStatus, dispatch]);

    // An audit link is a page of its own, also for somebody signed in: the
    // app has no screen at that path. Inside the session all the same, so a
    // sign-up that ends up here is still provisioned.
    const signedIn =
        auditTokenOf(window.location.pathname) === null ? (
            children
        ) : (
            <Suspense fallback={<Loading />}>
                <AuditSignedInFlow />
            </Suspense>
        );

    let content: ReactNode;
    if (isResolvingRedirect) {
        // Resolving the Google OAuth redirect.
        content = <Loading />;
    } else if (authStatus === "configuring") {
        // Restoring the session. Nothing of the app for someone signed out.
        content = hasStoredSession() ? restoring : <Loading />;
    } else if (authStatus === "unauthenticated") {
        content = (
            <Suspense fallback={<Loading />}>
                <authFlow.Component />
            </Suspense>
        );
    } else if (needsVerification) {
        // Authenticated, but a changed email still needs verifying.
        content = (
            <Suspense fallback={<Loading />}>
                <EmailCodeVerification />
            </Suspense>
        );
    } else if (serverSession) {
        // Mounted afresh on every arrival here -- a sign-in, a restored
        // session, a verified email change -- and each mount starts a session.
        content = <ServerSession>{signedIn}</ServerSession>;
    } else {
        content = signedIn;
    }

    return <main className="h-full">{content}</main>;
}

/**
 * Starts a management-api session before rendering anything that calls it.
 * Every other request only finds an existing account (CognitoAuthenticator on
 * the Api side), so this is where a first sign-in becomes one, and where a
 * changed name, picture or verified email reaches it.
 */
const ServerSession = ({ children }: { children: ReactNode }) => {
    const [startSession, { isError, error }] = useStartSessionMutation();
    // Persisted from the last session in this browser
    const returning = useSelector(getSelectedWorkspace) !== undefined;
    // Latched here rather than read back from the mutation. Switching
    // workspace resets the whole Api cache (store.ts), which wipes the
    // mutation's own isSuccess with it -- and nothing below re-runs the
    // effect, so the spinner would come back over a signed-in app and stay
    // there. Having started a session is a fact about this sign-in, not
    // cached server data.
    const [started, setStarted] = useState(false);
    const [missingToken, setMissingToken] = useState(false);
    const [attempt, setAttempt] = useState(0);

    useEffect(() => {
        let cancelled = false;

        fetchAuthSession()
            .then(({ tokens }) => {
                if (cancelled) {
                    return;
                }

                const idToken = tokens?.idToken?.toString();
                if (!idToken) {
                    setMissingToken(true);
                    return;
                }

                startSession({ idToken })
                    .unwrap()
                    .then(() => {
                        // The account exists now, so the sign-up attribution
                        // the request carried has done its job (baseQuery.ts)
                        deleteCookie("potentialSignUp");
                        deleteCookie("auditProspect");
                        deleteCookie(GA_SESSION_ID_COOKIE);
                        if (!cancelled) {
                            setStarted(true);
                        }
                    })
                    // A failure is rendered from the mutation's own state
                    .catch(() => undefined);
            })
            .catch(() => {
                if (!cancelled) {
                    setMissingToken(true);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [attempt, startSession]);

    if (started) {
        return children;
    }

    if (isError || missingToken) {
        return (
            <SessionFailed
                refusal={sessionRefusal(error)}
                onRetry={() => {
                    setMissingToken(false);
                    setAttempt((current) => current + 1);
                }}
            />
        );
    }

    return (
        <>
            {returning && <RequiredDataPrefetch />}
            <BootSkeleton />
        </>
    );
};

/**
 * What RequiredDataProvider loads first, asked for while the session starts
 * rather than after it: a persisted workspace means a returning session, whose
 * account exists already. A first sign-in waits, its account may not yet.
 * The session still has the last word (a refusal shows instead), and this one
 * load may predate a name or picture it copies from Cognito.
 */
const RequiredDataPrefetch = () => {
    useRequiredData();
    return null;
};

/**
 * What a refused session start means for the person, read from the answer.
 *
 * A 409: this sign-in's address already belongs to another account (the Api
 * keeps one account per address). Most likely a Google profile that was never
 * linked to the password account, so trying again cannot help; signing in the
 * way the person did before can.
 *
 * A 403 is single sign-on having a say (AuthSessionController): either the
 * organization's identity provider asserted an address that reaches no
 * account here, or the person signed in some other way than through the
 * single sign-on their organization uses -- in which case the answer names
 * the provider, and the screen can send them straight to it.
 */
type SessionRefusal =
    | { kind: "email_in_use" }
    | { kind: "sso_sign_in_refused"; message: string }
    | { kind: "sso_required"; message: string; providerName: string };

function sessionRefusal(error: unknown): SessionRefusal | null {
    if (!isApiError(error)) {
        return null;
    }
    const { status, data, code, message = "" } = error;
    if (status === 409) {
        return { kind: "email_in_use" };
    }
    if (status !== 403) {
        return null;
    }
    if (code === "sso_sign_in_refused") {
        return { kind: "sso_sign_in_refused", message };
    }
    // The provider is session-specific; the original error body is not camelized.
    if (
        code === "sso_required" &&
        typeof data === "object" &&
        data !== null &&
        "provider_name" in data &&
        typeof data.provider_name === "string"
    ) {
        return {
            kind: "sso_required",
            message,
            providerName: data.provider_name,
        };
    }
    return null;
}

function refusalCopy(refusal: SessionRefusal | null): {
    title: string;
    body: string;
} {
    if (refusal === null) {
        return {
            title: "We couldn't open your account",
            body: "Try again. If it keeps happening, sign out and sign back in.",
        };
    }
    switch (refusal.kind) {
        case "email_in_use":
            return {
                title: "This email address has another account",
                body: "Sign out, then sign in the way you did before. If you can't, email support@spectaclehq.com.",
            };
        case "sso_sign_in_refused":
            return {
                title: "Single sign-on can't sign you in",
                body: refusal.message,
            };
        case "sso_required":
            return {
                title: "Your organization uses single sign-on",
                body: refusal.message,
            };
    }
}

const SessionFailed = ({
    refusal,
    onRetry,
}: {
    refusal: SessionRefusal | null;
    onRetry: () => void;
}) => {
    const { signOut } = useSignOut();
    const dispatch = useDispatch();
    const [leaving, setLeaving] = useState(false);
    const { title, body } = refusalCopy(refusal);

    /**
     * Off to the organization's identity provider. Amplify refuses to start
     * a redirect while it can read a signed-in user from storage, and the
     * session this sign-in made is not the one wanted, so its tokens are
     * swept first. Swept, not signed out: after a sign-in through a redirect
     * (Google, which is how two of the three cases this screen exists for
     * arrive) Amplify's own sign-out is itself a navigation, to the pool
     * domain's logout endpoint, and would race the redirect below. The
     * refresh token left unrevoked belongs to a profile that reaches no
     * account.
     *
     * The workspace is forgotten too, as a sign-out would: the identity
     * single sign-on comes back as may reach no account until its session
     * links one, so it must not pass for a returning session (ServerSession).
     */
    const continueWithSso = async (providerName: string) => {
        setLeaving(true);
        try {
            dispatch(setSelectedWorkspace(undefined));
            clearCognitoLocalStorage();
            await continueWithSingleSignOn(providerName);
        } catch (err) {
            console.error("Could not continue with single sign-on:", err);
            setLeaving(false);
        }
    };

    // Signing out is the whole answer for the two refusals nothing else can
    // help with; beside another action it is the quiet option.
    const signOutIsTheAnswer =
        refusal?.kind === "email_in_use" ||
        refusal?.kind === "sso_sign_in_refused";

    return (
        <div className={"flex flex-col items-center justify-center h-full"}>
            <Panel className={"max-w-md"}>
                <div className="flex items-center justify-center mb-8">
                    <Logomark className="h-8" />
                </div>

                <div className="text-center">
                    <h2 className="text-xl font-semibold text-zinc-950 dark:text-white mb-2">
                        {title}
                    </h2>
                    <p className="text-sm text-zinc-600 dark:text-zinc-400">
                        {body}
                    </p>
                </div>

                <div className="mt-6 flex flex-col gap-3">
                    {refusal?.kind === "sso_required" && (
                        <Button
                            type="primary"
                            onClick={() =>
                                continueWithSso(refusal.providerName)
                            }
                            loading={leaving}
                            className="w-full"
                        >
                            Use single sign-on
                        </Button>
                    )}
                    {refusal === null && (
                        <Button
                            type="primary"
                            onClick={onRetry}
                            className="w-full"
                        >
                            Try again
                        </Button>
                    )}
                    {signOutIsTheAnswer ? (
                        <Button
                            type="primary"
                            onClick={signOut}
                            className="w-full"
                        >
                            Sign out
                        </Button>
                    ) : (
                        <Button plain onClick={signOut} className="w-full">
                            Sign out
                        </Button>
                    )}
                </div>
            </Panel>
        </div>
    );
};
